import { useCallback } from "react";
import { LessonParagraphNotes } from "../utils/lessonParagraphNotesStorage";
import { useDeviceProgress } from "./useDeviceProgress";
export const PARAGRAPH_NOTE_LIMIT = 400;

// FNV-1a 32-bit, base36-encoded - stable, deterministic, no crypto needed
// for a non-adversarial anchor id.
function hashText(text: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
}

// Stable per-paragraph id, e.g. "Lesson 3:h1a2b3c:o0" - keyed by a hash of
// the paragraph's own text (plus an occurrence count, for the rare case of
// two identical paragraphs in one lesson) rather than array index. A purely
// positional id (the original design) would silently reattach an existing
// note to the wrong paragraph if content is ever inserted/reordered/removed
// - which does happen to this file over time - whereas a note anchored to
// its own text only breaks if that exact paragraph's wording changes, which
// is the correct failure mode (the note simply stops matching anything,
// rather than misattaching to different text).
export function buildParagraphAnchorId(lessonNumber: string, paragraphText: string, occurrence = 0): string {
    return `${lessonNumber}:h${hashText(paragraphText)}:o${occurrence}`;
}

export function useLessonParagraphNotes() {
    const { data: notes, update, syncStatus } = useDeviceProgress<LessonParagraphNotes>("lessonParagraphNotes");
    const setNote = useCallback((key: string, text: string) => {
        const trimmed = text.slice(0, PARAGRAPH_NOTE_LIMIT).trim();
        void update(trimmed ? { kind: "set", key, value: { text: trimmed, updatedAt: new Date().toISOString() } } : { kind: "remove", key });
    }, [update]);
    const countForLesson = useCallback((lesson: string) => Object.keys(notes).filter(key => key.startsWith(`${lesson}:h`)).length, [notes]);
    return { notes, setNote, countForLesson, syncStatus };
}
